import { PositionsTable } from '@Components/Tables';
import {
    type ApiInvoice,
    type ApiInvoicePosition,
    EnumFrontendLayoutSetting,
    type EnumInvoiceType,
    type EnumProcessState,
    type EnumTaxationMode,
} from '@Model';
import { FrameInvoicePosition, invoicePositionColumns } from '@Modules/Billing';
import {
    type AsyncStateUpdate,
    type AsyncStateUpdateRequest,
    useEditSubObject,
} from '@synqo/frontend-component-library';
import { memo } from 'react';

// @ai-generated 2026-09-18T07:55:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5

interface IProps {
    baseApiObjectPath: string;
    requestUpdateState: AsyncStateUpdate<ApiInvoice>;
    stateQueueDepth: number;
    refetchData: () => Promise<boolean>;

    positions: ApiInvoicePosition[];
    taxationMode: EnumTaxationMode;
    processState: EnumProcessState;
    invoiceType: EnumInvoiceType;

    canEditPositions: boolean;
}

export const InvoicePositionsSection = memo(function InvoicePositionsSection({
    baseApiObjectPath,
    requestUpdateState,
    stateQueueDepth,
    refetchData,
    positions,
    taxationMode,
    processState,
    invoiceType,
    canEditPositions,
}: IProps) {
    const {
        addSubObject: addPosition,
        requestUpdateSubObject: requestUpdatePosition,
        deleteSubObjects: deletePositions,
    } = useEditSubObject<ApiInvoicePosition, ApiInvoice>(baseApiObjectPath, 'positions', requestUpdateState);

    // The server answers with the position only, yet a negative total turns the invoice into a credit note.
    const savePosition = async (updater: AsyncStateUpdateRequest<ApiInvoicePosition>, id: string) => {
        const saved = await requestUpdatePosition(updater, id);
        if (saved) {
            await refetchData();
        }
        return saved;
    };

    return (
        <PositionsTable<ApiInvoicePosition>
            layoutSettingsContext={EnumFrontendLayoutSetting.InvoicePositionsTableLayout}
            items={positions}
            columns={invoicePositionColumns(taxationMode)}
            name="InvoicePositions"
            editable={canEditPositions}
            canCreate={canEditPositions}
            onAdd={addPosition}
            onDelete={deletePositions}
            renderSubComponent={(row) => (
                <FrameInvoicePosition
                    key={row.id}
                    data={row}
                    meta={undefined}
                    canEdit={canEditPositions}
                    apiResourcePath={`${baseApiObjectPath}/positions`}
                    requestUpdateState={(u) => savePosition(u, row.id)}
                    stateQueueDepth={stateQueueDepth}
                    refetchData={refetchData}
                    priceDisplay={taxationMode}
                    processState={processState}
                    invoiceType={invoiceType}
                />
            )}
        />
    );
});
